import React, { useMemo } from "react";
import { Grid, Thumbnail } from "@culturehq/components";
import * as CHQ from "chq";

import lightbulb from "images/lightbulb.svg";

import { ProfileLink } from "app/links";
import { t, Trans } from "app/utils";

import { TimeDiff } from "components/common/Time";
import UserName from "components/common/UserName";

import EndorsementBody from "./EndorsementBody";
import styles from "./styles.module.scss";

// We can do this because right now we only support a single user receiving
// endorsements. When that changes in the future we're going to need to remove
// this function and instead dispay all of the receivers.
export const useEndorsementReceiver = (endorsement: CHQ.Endorsement) => useMemo(
  () => {
    if (!endorsement) {
      return null;
    }
    if (endorsement.userInterests.length > 0) {
      return endorsement.userInterests[0].user;
    }
    if (endorsement.userSkills.length > 0) {
      return endorsement.userSkills[0].user;
    }
    return null;
  },
  [endorsement]
);

type EndorsementProps = {
  children?: React.ReactNode;
  endorsement: CHQ.Endorsement;
};

const Endorsement: React.FC<EndorsementProps> = ({ children, endorsement }) => {
  const receiver = useEndorsementReceiver(endorsement);

  return (
    <>
      <div className="FeedItem__preview__title Endorsement__preview__title">
        {receiver && (
          <div className="FeedItem__user">
            <ProfileLink user={receiver}>
              <Thumbnail className="Thumbnail" image={receiver.avatar.thumbUrl} />
            </ProfileLink>
          </div>
        )}
        {children}
        {receiver && (
          <Trans>
            <ProfileLink user={receiver} /> received a spotlight
          </Trans>
        )}
        <p className="FeedItem__preview__title__date">
          <TimeDiff time={endorsement.createdAt} format="%B %e at %l:%M %p" />
        </p>
      </div>
      <Grid className="Endorsement__body">
        <Grid.Item xs={2}>
          <img className={styles.image} src={lightbulb} alt={t("Endorsement")} />
        </Grid.Item>
        <Grid.Item xs={10}>
          <EndorsementBody endorsement={endorsement} />
        </Grid.Item>
      </Grid>
      <p className="text-right">
        <Trans>
          From: <UserName as="span" user={endorsement.creator} />
        </Trans>
      </p>
    </>
  );
};

export default Endorsement;
